import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useExecuteExplorerQueryQuery,
  useGetExplorerTeamsQuery,
} from "../store/api/queryBuilderApi";
import {
  QUERY_PAGE_SIZE_OPTIONS,
  applyGridSortModel,
  cleanStatisticalFilters,
  cloneQueryParams,
  dismissFilterChip,
  parseQueryParamsFromSearch,
  toggleRecommendedFilter,
  toggleStatSelection,
  updateStatFilter,
} from "../utils/queryBuilder";
import { useSportWorkspace } from "./useSportWorkspace";
import { getQueryCatalog } from "../queryBuilder/getQueryCatalog";
import { applyGroupChange } from "../queryBuilder/applyGroupChange";
import { useNflLeagueScope } from "./useNflLeagueScope";
import { appendIncludeCflToSearchParams } from "../utils/nflLeagueScopeStorage";

export const useQueryBuilder = () => {
  const { sport } = useSportWorkspace();
  const catalog = getQueryCatalog(sport);
  const [searchParams, setSearchParams] = useSearchParams();
  const nflLeagueScope = useNflLeagueScope();

  const [queryParams, setQueryParams] = useState(() =>
    parseQueryParamsFromSearch(searchParams, catalog)
  );

  const prevSportRef = useRef(sport);

  useEffect(() => {
    if (prevSportRef.current !== sport) {
      prevSportRef.current = sport;
      const nextCatalog = getQueryCatalog(sport);
      const landing =
        nextCatalog.landingByGroup[nextCatalog.defaultGroupId] ||
        nextCatalog.landingByGroup[Object.keys(nextCatalog.landingByGroup)[0]];
      const next = cloneQueryParams(landing);
      paramsRef.current = next;
      setQueryParams(next);
    }
  }, [sport]);

  const leagueScopeArg = catalog.leagueScopeToggle
    ? { leagueScope: nflLeagueScope.leagueScope }
    : {};

  const { data, isFetching, error: queryError, refetch } =
    useExecuteExplorerQueryQuery({ sport, ...queryParams, ...leagueScopeArg });

  const teamsSkip = Array.isArray(catalog.teams);
  const { data: availableTeamsData, isLoading: isLoadingTeams } =
    useGetExplorerTeamsQuery(sport, { skip: teamsSkip });

  const displayedResultsRef = useRef(data);
  if (data) displayedResultsRef.current = data;

  const [activePresetId, setActivePresetId] = useState(null);
  const [shareNotice, setShareNotice] = useState(null);
  const paramsRef = useRef(queryParams);

  const queryResults = data ?? displayedResultsRef.current;
  const isExecuting = isFetching;

  const availableTeams = teamsSkip
    ? catalog.teams.map((t) => (typeof t === "object" ? t.id : t))
    : availableTeamsData?.teams || [];

  const updateURL = useCallback(
    (newParams) => {
      const params = new URLSearchParams();
      Object.entries(newParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (typeof value === "object") {
            if (Object.keys(value).length > 0) {
              params.set(key, JSON.stringify(value));
            }
          } else {
            params.set(key, value.toString());
          }
        }
      });
      if (catalog.leagueScopeToggle) {
        appendIncludeCflToSearchParams(params, nflLeagueScope.leagueScope);
      }
      setSearchParams(params, { replace: true });
    },
    [setSearchParams, catalog.leagueScopeToggle, nflLeagueScope.leagueScope]
  );

  useEffect(() => {
    paramsRef.current = queryParams;
  }, [queryParams]);

  useEffect(() => {
    updateURL(queryParams);
  }, [queryParams, updateURL]);

  useEffect(() => {
    const hasMalformedFilters = Object.values(queryParams.statisticalFilters).some(
      (filter) =>
        filter &&
        typeof filter === "object" &&
        Object.keys(filter).some(
          (key) => key.startsWith(">") || key.startsWith("<") || key.startsWith("=")
        )
    );
    if (hasMalformedFilters) {
      const next = {
        ...queryParams,
        statisticalFilters: cleanStatisticalFilters(queryParams.statisticalFilters),
      };
      paramsRef.current = next;
      setQueryParams(next);
    }
  }, [queryParams]);

  const handleShareSearch = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareNotice("Search link copied to clipboard");
    } catch {
      setShareNotice("Could not copy link — copy the URL from your browser");
    }
  }, []);

  const applyAndRun = useCallback((next) => {
    setActivePresetId(null);
    paramsRef.current = next;
    setQueryParams(next);
  }, []);

  const setNextQueryParams = useCallback((updater) => {
    const current = paramsRef.current;
    const next =
      typeof updater === "function" ? updater(current) : { ...current, ...updater };
    paramsRef.current = next;
    setQueryParams(next);
    return next;
  }, []);

  const patchAndRun = useCallback((updater) => {
    setActivePresetId(null);
    setNextQueryParams(updater);
  }, [setNextQueryParams]);

  const executeQueryHandler = useCallback(() => refetch(), [refetch]);

  const handleStatToggle = useCallback((stat) => {
    patchAndRun((prev) => toggleStatSelection(prev, stat));
  }, [patchAndRun]);

  const handleStatFilterChange = useCallback((stat, field, value) => {
    patchAndRun((prev) => updateStatFilter(prev, stat, field, value));
  }, [patchAndRun]);

  const handleBiographicalFilterChange = useCallback((field, value) => {
    patchAndRun((prev) => ({
      ...prev,
      biographicalFilters: {
        ...prev.biographicalFilters,
        [field]: value,
      },
    }));
  }, [patchAndRun]);

  const handleStatusFilterChange = useCallback((field, value) => {
    setActivePresetId(null);
    setNextQueryParams((prev) => ({
      ...prev,
      statusFilters: {
        ...prev.statusFilters,
        [field]: value,
      },
    }));
  }, [setNextQueryParams]);

  const handleYearChange = useCallback((field, value) => {
    setActivePresetId(null);
    setNextQueryParams((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, [setNextQueryParams]);

  const handleSortChange = useCallback((sortBy) => {
    const normalized =
      sortBy === "bio.birthName" ? catalog.nameSortField : sortBy;
    patchAndRun((prev) => ({ ...prev, sortBy: normalized }));
  }, [patchAndRun, catalog.nameSortField]);

  const handleSortDirectionChange = useCallback((direction) => {
    patchAndRun((prev) => ({ ...prev, sortDirection: direction }));
  }, [patchAndRun]);

  const handleSortModelChange = useCallback(
    (sortModel) => {
      const prev = paramsRef.current;
      const next = applyGridSortModel(prev, sortModel, catalog);
      if (
        next.sortBy === prev.sortBy &&
        Number(next.sortDirection) === Number(prev.sortDirection)
      ) {
        return;
      }
      patchAndRun(next);
    },
    [patchAndRun, catalog]
  );

  const handleLimitChange = useCallback(
    (limit) => {
      const nextLimit = Number(limit);
      if (!QUERY_PAGE_SIZE_OPTIONS.includes(nextLimit)) return;
      if (nextLimit === paramsRef.current.limit) return;
      patchAndRun({ limit: nextLimit });
    },
    [patchAndRun]
  );

  const handleQueryTypeChange = useCallback((value) => {
    patchAndRun((prev) => ({ ...prev, queryType: value }));
  }, [patchAndRun]);

  const handleTeamsChange = useCallback((teams) => {
    patchAndRun({ teams });
  }, [patchAndRun]);

  const handleRecommendedToggle = useCallback((filterId) => {
    patchAndRun((prev) => toggleRecommendedFilter(prev, filterId, catalog));
  }, [patchAndRun, catalog]);

  const handleDismissChip = useCallback((chipKey) => {
    patchAndRun((prev) => dismissFilterChip(prev, chipKey, catalog));
  }, [patchAndRun, catalog]);

  const handleYearRangeCommit = useCallback(({ startYear, endYear }) => {
    patchAndRun({ startYear, endYear });
  }, [patchAndRun]);

  const handleGroupChange = useCallback((groupId) => {
    const next = applyGroupChange(paramsRef.current, groupId, catalog);
    applyAndRun(next);
  }, [applyAndRun, catalog]);

  const resetQuery = useCallback(() => {
    const landing =
      catalog.landingByGroup[catalog.defaultGroupId] ||
      catalog.landingByGroup[Object.keys(catalog.landingByGroup)[0]];
    return applyAndRun(cloneQueryParams(landing));
  }, [applyAndRun, catalog]);

  const handleClearAll = resetQuery;

  const applyPreset = useCallback((preset) => {
    const landing =
      catalog.landingByGroup[catalog.defaultGroupId] ||
      catalog.landingByGroup[Object.keys(catalog.landingByGroup)[0]];
    const nextParams = {
      ...landing,
      ...preset.params,
      biographicalFilters: preset.params.biographicalFilters || {},
      statusFilters: preset.params.statusFilters || {},
      teams: preset.params.teams || [],
    };
    setActivePresetId(preset.id);
    paramsRef.current = nextParams;
    setQueryParams(nextParams);
    return nextParams;
  }, [catalog]);

  const applyPresetAndRun = useCallback((preset) => {
    applyPreset(preset);
  }, [applyPreset]);

  const dismissShareNotice = useCallback(() => setShareNotice(null), []);

  return {
    sport,
    catalog,
    queryParams,
    queryResults,
    availableTeams,
    activePresetId,
    shareNotice,
    isExecuting,
    isLoadingTeams,
    queryError,
    nflLeagueScope,
    executeQueryHandler,
    handleShareSearch,
    applyAndRun,
    handleStatToggle,
    handleStatFilterChange,
    handleBiographicalFilterChange,
    handleStatusFilterChange,
    handleYearChange,
    handleSortChange,
    handleSortDirectionChange,
    handleSortModelChange,
    handleLimitChange,
    handleQueryTypeChange,
    handleTeamsChange,
    handleRecommendedToggle,
    handleDismissChip,
    handleClearAll,
    handleYearRangeCommit,
    handleGroupChange,
    resetQuery,
    applyPreset,
    applyPresetAndRun,
    dismissShareNotice,
  };
};
